// The header's VIN edit (2026-10-21): any agent edits or adds the contact's
// VIN. A pencil after the VIN's copy/decode icons ("+ Add VIN" with no VIN)
// turns it into a box with Save and Cancel (Enter saves, Esc cancels) and a
// live helper. A failed check digit can be saved after one confirm. After a
// save the VIN carries an "Edited by <first name>" chip. Clear restores the
// VIN CXHub found. Saving never regenerates: onSaved lets the header offer
// Regenerate. The text and checks are in vinEdit.js.
import { useEffect, useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { setVinOverride } from './api.js'
import { vinEditHelp, normalizeVinInput, vinEditedChip, VIN_CONFIRM_QUESTION, VIN_LENGTH } from './vinEdit.js'

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

const TONE_COLOR = { neutral: 'text.secondary', error: 'error.main', success: 'success.main', warning: 'warning.dark' }

// The chip beside an edited VIN. Rendered by the parent next to the VIN.
export function VinEditedChip({ vinOverride }) {
  const chip = vinEditedChip(vinOverride)
  if (!chip) return null
  return (
    <Tooltip title={chip.tooltip}>
      <Chip size="small" variant="outlined" color={chip.color} label={chip.label} data-testid="vin-edited-chip" sx={{ height: 20, fontSize: 11 }} />
    </Tooltip>
  )
}

// children: the VIN as the header shows it (link, copy, decode). Hidden while
// editing. vin: the VIN showing now (null when none). vinOverride: the
// conversation's override, for the chip and Clear.
export default function VinEdit({ conversationId, vin, vinOverride, onSaved, children }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // A different contact never inherits an open edit.
  useEffect(() => { setEditing(false); setText(''); setError(null); setConfirmOpen(false) }, [conversationId])

  const help = vinEditHelp(text)

  function start() {
    setText(normalizeVinInput(vin ?? ''))
    setError(null)
    setEditing(true)
  }
  function cancel() {
    setEditing(false)
    setError(null)
    setConfirmOpen(false)
  }
  async function send(value) {
    setBusy(true); setError(null)
    try {
      const res = await setVinOverride(conversationId, value)
      setEditing(false)
      setConfirmOpen(false)
      onSaved?.(res, { cleared: value === null })
    } catch (e) {
      setError(e.message)
      setConfirmOpen(false)
    } finally {
      setBusy(false)
    }
  }
  function save() {
    if (!help.canSave || busy) return
    if (help.checkDigitOk === false) { setConfirmOpen(true); return }
    send(help.value)
  }

  if (!editing) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" data-testid="vin-edit-view">
        {vin ? children : null}
        {vin ? (
          <Tooltip title="Edit VIN">
            <IconButton size="small" onClick={start} aria-label="Edit VIN" data-testid="vin-edit-button" sx={{ p: 0.25 }}>
              <PencilIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button size="small" onClick={start} data-testid="vin-add-button" sx={{ py: 0, minWidth: 0, textTransform: 'none', fontSize: 13 }}>+ Add VIN</Button>
        )}
        {vin && <VinEditedChip vinOverride={vinOverride} />}
      </Stack>
    )
  }

  return (
    <Box data-testid="vin-edit-form" sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          autoFocus
          value={text}
          onChange={(e) => setText(normalizeVinInput(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save() }
            if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          inputProps={{ maxLength: VIN_LENGTH, 'aria-label': 'VIN', 'data-testid': 'vin-edit-input', spellCheck: false, style: { fontFamily: 'monospace', textTransform: 'uppercase', fontSize: 13, padding: '2px 6px', width: '19ch' } }}
          disabled={busy}
        />
        <Button size="small" variant="contained" onClick={save} disabled={!help.canSave || busy} data-testid="vin-edit-save">Save</Button>
        <Button size="small" onClick={cancel} disabled={busy} data-testid="vin-edit-cancel">Cancel</Button>
        {vinOverride?.vin && (
          <Tooltip title="Restore the VIN CXHub found">
            <span>
              <Button size="small" color="inherit" onClick={() => send(null)} disabled={busy} data-testid="vin-edit-clear">Clear</Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Box data-testid="vin-edit-help" sx={{ mt: 0.25 }}>
        {help.lines.map((l) => (
          <Typography key={l.text} variant="caption" display="block" sx={{ color: TONE_COLOR[l.tone], lineHeight: 1.3 }}>{l.text}</Typography>
        ))}
        {error && <Typography variant="caption" display="block" color="error">{error}</Typography>}
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          <DialogContentText>{VIN_CONFIRM_QUESTION}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} data-testid="vin-confirm-back">Go back</Button>
          <Button variant="contained" color="warning" onClick={() => send(help.value)} disabled={busy} data-testid="vin-confirm-save">Save anyway</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
